import { EditButton } from "@components/buttons/edit";
import {
  SHBox,
  SHButton,
  SHDialog,
  SHImage,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { SHUploadFile } from "@components/design-systems/sh-upload-file";
import { PhotoSVG } from "@components/svgs";
import { DateFormat, DateTimeFormat } from "@constants/format";
import { usePractice } from "@hooks/usePractice";
import { useUserPermissions } from "@hooks/userUserPermission";
import { useUpload } from "@hooks/useUpload";
import {
  AnalysisSummaryDTO,
  RenderAnalysisSummaryItemProps,
} from "@models/platform-analysis/entities/steps/summary";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { RootState } from "@redux/store";
import { format } from "date-fns";
import { isEqual } from "lodash";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { FileRejection } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";

export const SummaryAdviserInformation = memo(
  ({ disabled: sectionDisabled }: RenderAnalysisSummaryItemProps) => {
    const { setValue } = useFormContext<AnalysisSummaryDTO>();
    const { isUploading, uploadOne } = useUpload();
    const { practice, updateLogo } = usePractice();
    const { isAdminGroup, isAdviserAdminGroup } = useUserPermissions();

    const storedDatabaseVersion = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.databaseVersion,
    );
    const storedSummaryData = useSelector(
      (state: RootState) => state.platformAnalysis.platformAnalysis?.summary,
    );

    const practiceLogoRef = useRef(storedSummaryData?.firmLogo || "");
    const [showConfirmPopup, toggleConfirmPopupOpen] = useToggle(false);
    const [showUploadPopup, toggleShowUploadPopup] = useToggle(false);
    const [isProcessingUpdateLogo, toggleIsProcessingUpdateLogo] =
      useToggle(false);
    const [originalLogo, setOriginalLogo] = useState<string>(
      storedSummaryData?.firmLogo || "",
    );

    const handleOnUploadImage = async (
      acceptedFiles: File[],
      fileRejections: FileRejection[],
    ) => {
      const s3File = await uploadOne(acceptedFiles, fileRejections);
      if (s3File) practiceLogoRef.current = s3File.url;
    };

    const handleOnCancelUploadImage = () => {
      practiceLogoRef.current = originalLogo;
      toggleShowUploadPopup(false);
    };

    const handleOnProcessUpdateLogo = async () => {
      const newLogoStr = practiceLogoRef.current;
      const practiceId = practice?.id;
      if (!practiceId || !newLogoStr) return;

      updateLogo({ newLogoStr, practiceId }).then((res) => {
        if (res) setOriginalLogo(newLogoStr);
        toggleConfirmPopupOpen(false);
        toggleShowUploadPopup(false);
        toggleIsProcessingUpdateLogo(false);
      });
    };

    const {
      name,
      ownerName,
      ownerARN,
      lastModifiedDate,
      serial,
      firmName,
      firmAfsl,
      familyGroupName,
      familyMembersNames,
    } = useMemo(() => {
      const {
        name,
        ownerName,
        ownerARN,
        lastModifiedDate,
        serial,
        firmName,
        firmAFSL,
        familyGroupName,
        familyMembersNames,
      } = storedSummaryData || {};

      return {
        name: name || "",
        ownerName: ownerName || "",
        ownerARN: ownerARN || "",
        lastModifiedDate: lastModifiedDate || new Date(),
        serial: serial || "",
        firmName: firmName || "",
        firmAfsl: firmAFSL || "",
        familyGroupName: familyGroupName || "",
        familyMembersNames: familyMembersNames || [],
      };
    }, [storedSummaryData]);

    const shouldShowEditButton = useMemo(() => {
      return !sectionDisabled && (isAdminGroup || isAdviserAdminGroup);
    }, [sectionDisabled, isAdminGroup, isAdviserAdminGroup]);

    useEffect(() => {
      setValue("firmLogo", practiceLogoRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceLogoRef.current]);

    return (
      <SHStack gap={2}>
        <SHStack direction={"row"} gap={2}>
          <SHStack direction={"row"}>
            <SHStack direction={"row"} gap={2}>
              <SHStack position={"relative"} direction={"row"}>
                <SHImage
                  width={100}
                  height={100}
                  src={practiceLogoRef.current}
                />

                {shouldShowEditButton && (
                  <SHBox position={"absolute"} top={-10} right={-10}>
                    <EditButton
                      height="25px"
                      width="25px"
                      onClick={toggleShowUploadPopup}
                    />
                  </SHBox>
                )}
              </SHStack>
            </SHStack>
          </SHStack>

          <SHStack
            flexDirection={"column"}
            justifyContent={"center"}
            width={"fit-content"}
          >
            <SHTypography
              disabled={sectionDisabled}
              variant="body1"
              fontSize={"24px"}
              paddingTop={"0px"}
            >
              {name}
            </SHTypography>

            <SHStack flexDirection={"row"} gap={"15px"}>
              <SHStack flexDirection={"row"} gap={"5px"}>
                <SHTypography
                  disabled={sectionDisabled}
                  variant="body2"
                  fontWeight={600}
                >
                  Prepared by:
                </SHTypography>

                <SHTypography disabled={sectionDisabled} variant="body2">
                  {`${ownerName} of ${firmName}`}
                </SHTypography>
              </SHStack>

              <SHTypography disabled={sectionDisabled} variant="body2">
                |
              </SHTypography>

              <SHStack flexDirection={"row"} gap={"5px"}>
                <SHTypography
                  disabled={sectionDisabled}
                  variant="body2"
                  fontWeight={600}
                >
                  ARN:
                </SHTypography>
                <SHTypography disabled={sectionDisabled} variant="body2">
                  {ownerARN}
                </SHTypography>
              </SHStack>

              <SHTypography disabled={sectionDisabled} variant="body2">
                |
              </SHTypography>

              <SHStack flexDirection={"row"} gap={"5px"}>
                <SHTypography
                  disabled={sectionDisabled}
                  variant="body2"
                  fontWeight={600}
                >
                  AFSL No:
                </SHTypography>
                <SHTypography disabled={sectionDisabled} variant="body2">
                  {firmAfsl}
                </SHTypography>
              </SHStack>
            </SHStack>

            <SHStack flexDirection={"row"} gap={"15px"}>
              <SHStack flexDirection={"row"} gap={"5px"}>
                <SHTypography
                  disabled={sectionDisabled}
                  variant="body2"
                  fontWeight={600}
                >
                  Last edit:
                </SHTypography>

                <SHTypography disabled={sectionDisabled} variant="body2">
                  {format(lastModifiedDate, DateTimeFormat)}
                </SHTypography>
              </SHStack>

              <SHTypography disabled={sectionDisabled} variant="body2">
                |
              </SHTypography>
              <SHStack flexDirection={"row"} gap={"5px"}>
                <SHTypography
                  disabled={sectionDisabled}
                  variant="body2"
                  fontWeight={600}
                >
                  Data valid:
                </SHTypography>

                <SHTypography disabled={sectionDisabled} variant="body2">
                  {format(storedDatabaseVersion || new Date(), DateFormat)}
                </SHTypography>
              </SHStack>

              <SHTypography disabled={sectionDisabled} variant="body2">
                |
              </SHTypography>

              <SHStack flexDirection={"row"} gap={"5px"}>
                <SHTypography
                  disabled={sectionDisabled}
                  variant="body2"
                  fontWeight={600}
                >
                  Serial:
                </SHTypography>
                <SHTypography disabled={sectionDisabled} variant="body2">
                  {serial}
                </SHTypography>
              </SHStack>
            </SHStack>
          </SHStack>
        </SHStack>

        <SHStack flexDirection={"column"} gap={"5px"}>
          <SHTypography disabled={sectionDisabled} variant="subtitle2">
            {`This Suitability Review is for ${familyGroupName} and the
              following individuals/entities:`}
          </SHTypography>

          <SHTypography
            disabled={sectionDisabled}
            variant="body2"
            fontWeight={600}
          >
            {familyMembersNames.join(", ").replace(/, (?!.*,)/g, " and ")}
          </SHTypography>
        </SHStack>

        <SHDialog
          open={showUploadPopup}
          disableEscapeKeyDown
          disableBackdropClick
          maxWidth={false}
          sx={{
            "& .MuiPaper-root": {
              width: "fit-content",
              height: "auto",
              paddingX: 2,
              paddingY: 0,
            },
          }}
        >
          <DialogTitle>
            <SHTypography variant="h2" fontWeight={700}>
              Update firm's logo
            </SHTypography>
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              paddingX: 0,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SHStack
              width={"100%"}
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              gap={2}
            >
              <SHImage width={150} height={150} src={practiceLogoRef.current} />
              <SHUploadFile
                width={250}
                height={150}
                isUploading={isUploading}
                fileIcon={<PhotoSVG />}
                dropzoneOptions={{
                  multiple: false,
                  accept: { "image/*": [".svg", ".png", ".jpg", ".gif"] },
                  onDrop: handleOnUploadImage,
                  disabled: isUploading,
                }}
              />
            </SHStack>
          </DialogContent>

          <DialogActions sx={{ paddingY: 0 }}>
            <SHStack
              gap={2}
              paddingBottom={2}
              direction={"row"}
              width={"100%"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <SHButton
                disabled={isUploading}
                size="extraMedium"
                onClick={handleOnCancelUploadImage}
              >
                Cancel
              </SHButton>
              <SHButton
                size="extraMedium"
                variant={"contained"}
                disabled={
                  isUploading || isEqual(practiceLogoRef.current, originalLogo)
                }
                onClick={() => toggleConfirmPopupOpen(true)}
              >
                Save
              </SHButton>
            </SHStack>
          </DialogActions>
        </SHDialog>

        <SHDialog
          disableEscapeKeyDown
          disableBackdropClick
          maxWidth={false}
          open={showConfirmPopup}
          header={
            "This will update your firm's logo on SuitabilityHub. Proceed?"
          }
          footer={
            <SHStack
              justifyContent="center"
              alignItems="center"
              direction={"row"}
              spacing={7}
            >
              <SHButton
                disabled={isProcessingUpdateLogo}
                color="secondary"
                variant="text"
                size="extraMedium"
                onClick={() => {
                  toggleConfirmPopupOpen(false);
                  handleOnCancelUploadImage();
                }}
              >
                Cancel
              </SHButton>

              <SHButton
                color="primary"
                size="extraMedium"
                variant="contained"
                isLoading={isProcessingUpdateLogo}
                disabled={isProcessingUpdateLogo}
                onClick={async () => {
                  toggleIsProcessingUpdateLogo(true);
                  setTimeout(() => {
                    handleOnProcessUpdateLogo();
                  }, 0);
                }}
              >
                Proceed
              </SHButton>
            </SHStack>
          }
        />
      </SHStack>
    );
  },
);
